import type { ComputedRef } from 'vue';
import type { CompositePrimaryKey, Filter } from '#layers/data-model/public';
import type { RelatedItemOperator } from '#layers/filters/components/Filter/utils/related-item-match';
import type { RelatedItemClause, RelatedItemMatch } from '#layers/filters/composables/use-related-item-match';
import { computed, ref, watch } from 'vue';
import { FilterOperator } from '#layers/data-model/public';
import { isExclusionOperator } from '#layers/filters/components/Filter/utils/related-item-match';

interface RelatedItemClauseEditorOptions {
	serialize: (match: RelatedItemMatch) => Filter;
	onChange: (filter: Filter) => void;
}

export function useRelatedItemClauseEditor(
	clause: ComputedRef<RelatedItemClause | null>,
	options: RelatedItemClauseEditorOptions,
) {
	const operatorOverride = ref<RelatedItemOperator>();

	watch(() => clause.value?.target.relationFieldPath.join('.'), () => {
		operatorOverride.value = undefined;
	});

	const writableOperator = computed<RelatedItemOperator | undefined>({
		get() {
			const current = clause.value;
			if (!current) return;

			// An empty to-one selection has lost its inline polarity (reparses as `_eq`); fall back to the
			// remembered operator so a negated clause stays "is not". A to-many clause carries polarity on
			// its `_some`/`_none` quantifier, so it round-trips without help.
			if (!current.traversesToMany && current.keys.length === 0) {
				return operatorOverride.value ?? current.operator;
			}

			return current.operator;
		},
		set(operator) {
			const current = clause.value;
			if (!operator || !current) return;

			// Narrowing to a single-value operator keeps only the first item.
			let keys = current.keys;
			if ((operator === FilterOperator.EQ || operator === FilterOperator.NEQ) && keys.length > 1) keys = keys.slice(0, 1);

			emitClause({ operator, keys });
		},
	});

	function emitClause(overrides: { operator?: RelatedItemOperator; keys?: CompositePrimaryKey[] }) {
		const current = clause.value;
		if (!current) return;

		const operator = overrides.operator ?? current.operator;
		operatorOverride.value = operator; // remember polarity so an emptied selection doesn't reset it to `_eq`

		options.onChange(options.serialize({
			target: current.target,
			operator,
			keys: overrides.keys ?? current.keys,
		}));
	}

	function onKeysUpdate(keys: CompositePrimaryKey[]) {
		if (!clause.value) return;

		emitClause({ operator: membershipOperatorFor(writableOperator.value, keys), keys });
	}

	return { writableOperator, onKeysUpdate };
}

// The operator stays consistent with the item count while preserving polarity: single-value
// (`_eq`/`_neq`) and list (`_in`/`_nin`) swap as items are added or removed.
function membershipOperatorFor(operator: RelatedItemOperator | undefined, keys: CompositePrimaryKey[]): RelatedItemOperator {
	const isExclusion = !!operator && isExclusionOperator(operator);

	if (keys.length < 2) return isExclusion ? FilterOperator.NEQ : FilterOperator.EQ;

	return isExclusion ? FilterOperator.NIN : FilterOperator.IN;
}
